"use client";

import { useEffect, useMemo, useState } from "react";
import { NicheFilterBar } from "@/components/niches/niche-filters";
import { NicheTable } from "@/components/niches/niche-table";
import { OpportunityChart } from "@/components/charts/opportunity-chart";
import { MrrDistributionChart } from "@/components/charts/mrr-distribution-chart";
import { TemplateCards } from "@/components/opportunities/template-cards";
import { formatCompactNumber, formatDelta, formatMoney, formatRatioPercent } from "@/lib/format";
import type { Entitlements, FetchFailure, NicheFilters, NicheRanking, OpportunityTemplate } from "@/lib/types";

type ApiResponse = {
  source: string;
  generatedAt: string;
  count: number;
  totals: {
    niches: number;
    startups: number;
  };
  failures: FetchFailure[];
  templates: OpportunityTemplate[];
  entitlements: Entitlements;
  data: NicheRanking[];
  details?: string;
  error?: string;
};

const DEFAULT_FILTERS: NicheFilters = {
  minScore: null,
  minGrowth: null,
  maxStartups: null,
  minForSaleRatio: null,
  minGapScore: null,
  improvingOnly: false,
  highAcquisitionOnly: false,
  template: null,
  sortBy: "opportunityScore"
};

export function LeaderboardShell() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [niches, setNiches] = useState<NicheRanking[]>([]);
  const [templates, setTemplates] = useState<OpportunityTemplate[]>([]);
  const [failures, setFailures] = useState<FetchFailure[]>([]);
  const [source, setSource] = useState("unknown");
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [filters, setFilters] = useState<NicheFilters>(DEFAULT_FILTERS);
  const [viewerEmail] = useState("");
  const [historyDays] = useState(30);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const response = await fetch(`/api/niches?days=${historyDays}`, {
          cache: "no-store",
          headers: viewerEmail ? { "x-nichemrr-email": viewerEmail } : undefined
        });
        const payload = (await response.json()) as ApiResponse;

        if (!response.ok) {
          throw new Error(payload.details ?? payload.error ?? "Unable to load niches.");
        }

        setNiches(payload.data ?? []);
        setTemplates(payload.templates ?? []);
        setFailures(payload.failures ?? []);
        setSource(payload.source ?? "unknown");
        setGeneratedAt(payload.generatedAt ?? null);
        setEntitlements(payload.entitlements ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load niches.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [viewerEmail, historyDays]);

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === filters.template) ?? null,
    [filters.template, templates]
  );

  const filtered = useMemo(() => {
    const templateCategories = activeTemplate ? new Set(activeTemplate.niches.map((niche) => niche.category)) : null;

    const data = niches.filter((niche) => {
      if (filters.minScore !== null && niche.opportunityScore < filters.minScore) return false;
      if (filters.minGrowth !== null && (niche.averageGrowth30d ?? Number.NEGATIVE_INFINITY) < filters.minGrowth) return false;
      if (filters.maxStartups !== null && niche.startupCount > filters.maxStartups) return false;
      if (filters.minForSaleRatio !== null && niche.forSaleRatio * 100 < filters.minForSaleRatio) return false;
      if (filters.highAcquisitionOnly && niche.forSaleRatio < 0.15) return false;
      if (filters.minGapScore !== null && (niche.gapScore ?? 0) < filters.minGapScore) return false;
      if (filters.improvingOnly && (niche.delta?.delta ?? Number.NEGATIVE_INFINITY) <= 0) return false;
      if (templateCategories && !templateCategories.has(niche.category)) return false;
      return true;
    });

    return [...data].sort((left, right) => {
      if (filters.sortBy === "delta") {
        return (right.delta?.delta ?? Number.NEGATIVE_INFINITY) - (left.delta?.delta ?? Number.NEGATIVE_INFINITY);
      }

      if (filters.sortBy === "gapScore") {
        const leftVal = left.gapScore ?? Number.NEGATIVE_INFINITY;
        const rightVal = right.gapScore ?? Number.NEGATIVE_INFINITY;
        return rightVal - leftVal;
      }

      const key = filters.sortBy;
      const leftValue = key === "averageGrowth30d" ? left.averageGrowth30d ?? Number.NEGATIVE_INFINITY : left[key];
      const rightValue = key === "averageGrowth30d" ? right.averageGrowth30d ?? Number.NEGATIVE_INFINITY : right[key];
      return Number(rightValue) - Number(leftValue);
    });
  }, [activeTemplate, filters, niches]);

  const totalTrackedMrr = filtered.reduce((sum, niche) => sum + niche.totalMrr, 0);
  const averageScore = filtered.length > 0
    ? filtered.reduce((sum, niche) => sum + niche.opportunityScore, 0) / filtered.length
    : 0;
  const risingNiche = [...filtered]
    .sort((a, b) => (b.delta?.delta ?? Number.NEGATIVE_INFINITY) - (a.delta?.delta ?? Number.NEGATIVE_INFINITY))[0] ?? null;
  const highLiquidityCount = filtered.filter((niche) => niche.forSaleRatio >= 0.15).length;

  if (loading && niches.length === 0) {
    return (
      <main className="container">
        <section className="hero">
          <h1>NicheMRR</h1>
          <p className="hero-sub">
            Ranking every TrustMRR-backed niche so you can decide where to build faster.
          </p>
        </section>
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Building the niche leaderboard...</span>
        </div>
      </main>
    );
  }

  if (error && niches.length === 0) {
    return (
      <main className="container">
        <section className="hero">
          <h1>NicheMRR</h1>
          <p className="hero-sub">
            TrustMRR-backed niche discovery, ranking, and founder benchmarks.
          </p>
        </section>
        <div className="error-card">{error}</div>
      </main>
    );
  }

  return (
    <main className="container">
      <section className="hero">
        <h1>NicheMRR</h1>
        <p className="hero-sub">
          Discover the best SaaS markets by score, momentum, crowding, and verified revenue.
        </p>
        <p className="hero-meta">
          {source === "cache" ? "Cached" : "Fresh"} leaderboard
          {generatedAt ? ` | ${new Date(generatedAt).toLocaleString()}` : ""}
        </p>
      </section>

      <section className="stats">
        <div className="stat">
          <p className="stat-label">Niches ranked</p>
          <p className="stat-value">{filtered.length}</p>
          <p className="stat-meta">Across the TrustMRR category set</p>
        </div>
        <div className="stat">
          <p className="stat-label">Tracked MRR</p>
          <p className="stat-value stat-value--success">{formatMoney(totalTrackedMrr)}</p>
          <p className="stat-meta">Verified recurring revenue in view</p>
        </div>
        <div className="stat">
          <p className="stat-label">Average score</p>
          <p className="stat-value stat-value--accent">{averageScore.toFixed(1)}</p>
          <p className="stat-meta">Across the filtered leaderboard</p>
        </div>
        <div className="stat">
          <p className="stat-label">Rising this week</p>
          <p className="stat-value stat-value--cyan">{risingNiche ? risingNiche.label : "-"}</p>
          <p className="stat-meta">{risingNiche ? `${formatDelta(risingNiche.delta?.delta ?? null)} score` : "No historical delta yet"}</p>
        </div>
        <div className="stat">
          <p className="stat-label">Liquid niches</p>
          <p className="stat-value">{formatCompactNumber(highLiquidityCount)}</p>
          <p className="stat-meta">Niches with 15%+ startups for sale</p>
        </div>
      </section>

      {failures.length > 0 && (
        <div className="card leaderboard-warning">
          <p className="card-title">Partial leaderboard</p>
          <p className="card-desc">
            {failures.length} categories failed to load. The leaderboard still shows the remaining niches.
          </p>
        </div>
      )}

      <p className="section-label">Suggested ways to use this market map</p>
      <TemplateCards
        templates={templates}
        activeTemplate={filters.template}
        onSelectTemplate={(template) => setFilters((prev) => ({ ...prev, template }))}
      />

      <p className="section-label">Leaderboard controls</p>
      <NicheFilterBar filters={filters} onChange={setFilters} />

      <div className="dashboard-grid">
        <OpportunityChart niches={filtered} />
        <MrrDistributionChart niches={filtered} />
      </div>

      <p className="section-label">Top opportunities</p>
      <NicheTable niches={filtered} sortBy={filters.sortBy} onSort={(sortBy) => setFilters((prev) => ({ ...prev, sortBy }))} />

      <section className="leaderboard-footnote">
        <p>
          Opportunity score blends median MRR, growth, total MRR, crowding, and sale liquidity. For sale ratio is shown as {formatRatioPercent(0.15)} style percentages.
        </p>
      </section>
    </main>
  );
}
